/**
 * Chart-of-accounts endpoints — request schemas + query schemas.
 *
 * READS are plain RLS-scoped queries/RPCs (org RLS authorizes). WRITES are
 * Pattern B/B+: the `chart_of_accounts` table carries RESTRICTIVE
 * verify_backend_token() policies, so only the Hono backend can INSERT/UPDATE/
 * DELETE. Field-level safety is the Zod whitelist below; the table's own BEFORE
 * triggers (number assignment, hierarchy/categorization validation, path,
 * delete guards) and the audit trigger still fire on every write. The one Pattern C exception is
 * classification on edit (type/subtype/parent → `update_account_classification`,
 * see updateAccountSchema). See docs/BACKEND_API.md → "Pattern B+" and
 * docs/SUPABASE_SECURITY.md §5.
 *
 * Response shapes are NOT redeclared here — the web hook casts each envelope to
 * the existing `@/types/financial-accounts` types (ChartOfAccount,
 * FinancialAccountCategory, …), so there is one source of truth for those.
 */
import { z } from "zod";
import { SUPPORTED_CURRENCIES } from "../format/currency.js";
import { ACCOUNT_NUMBERING_SCHEMES } from "../lib/accountNumbers.js";
const uuid = z.string().uuid();
/**
 * Create-account body. Mirrors the payload `CreateOrUpdateAccountModal` sends
 * (CreateAccountData). Server-set columns (organization_id, account_number,
 * account_level, is_active, current_balance) are deliberately NOT accepted — the
 * route sets them or the BEFORE triggers derive them (the number from the
 * subtype's house code, the level from the parent). `subtype_id` is optional:
 * omitted, the trigger fills the type's general subtype; given, the route
 * refuses one outside the org's jurisdiction (SUBTYPE_NOT_AVAILABLE). is_system_account is a GENERATED column
 * (system_account_type IS NOT NULL), so it is never written at all. category_id/
 * type_id are required NOT-NULL uuids (sub-accounts inherit-and-send a valid
 * classification too).
 *
 * opening_balance/opening_balance_date are NOT columns anymore (retired,
 * 20260804095000): the route forwards them to set_account_opening_balance,
 * which posts a tagged journal against the Opening Balance Equity account.
 * A non-zero amount requires the date (it becomes the journal date).
 */
export const createAccountSchema = z
    .object({
    account_name: z.string().min(1),
    account_description: z.string().nullish(),
    category_id: uuid,
    type_id: uuid,
    subtype_id: uuid.nullish(),
    parent_account_id: uuid.nullish(),
    normal_balance: z.enum(["debit", "credit"]),
    can_have_children: z.boolean().optional(),
    requires_project: z.boolean().optional(),
    opening_balance: z.number().optional(),
    opening_balance_date: z.string().nullish(),
    default_tax_rate_id: uuid.nullish(),
    currency: z.string().nullish(),
})
    .strict();
/**
 * A chart-of-accounts PLAN — what Ré's `propose_accounts` tool previews and the
 * card's Create button commits, through the one RPC `apply_chart_accounts_plan`
 * (dry run for the preview, commit with the previewed hash). The model decides
 * names, subtypes, parents and currencies only: no number, type, category,
 * normal balance or description — the database derives classification from
 * the subtype (trg_assign_account_number) and the number from it.
 *
 * Rule homes (docs/CHART_OF_ACCOUNTS.md):
 *   - the INTRA-PLAN rules live here and nowhere else (this superRefine):
 *     unique refs, every ref parent exists in the plan, no cycles, a
 *     top-level row has a subtype, the currency is supported;
 *   - subtype availability for the org: assertSubtypesAvailable (apps/api);
 *   - org-dependent rules (depth, system parent, duplicate names, empty
 *     chart, a child's subtype — null or its parent's, or a CONTRA subtype of
 *     the parent's type, else CHILD_SUBTYPE_NOT_CONTRA): the RPC (the child
 *     rule needs the parent's type, which only the database knows for an
 *     existing parent).
 */
export const COA_PLAN_MAX_ACCOUNTS = 40;
const coaPlanParentSchema = z.union([
    z.object({ account_id: uuid }).strict(),
    z.object({ ref: z.string().min(1).max(64) }).strict(),
]);
const coaPlanAccountSchema = z
    .object({
    /** Plan-local id, e.g. "a1" — how a row in the same plan names it as parent. */
    ref: z.string().min(1).max(64),
    name: z.string().trim().min(1).max(100),
    /**
     * Required on a top-level row. On a child: null (it inherits its
     * parent's), or a CONTRA subtype of the parent's type when the child is
     * that parent's contra account (Owner draws under Owner capital,
     * accumulated depreciation under the asset) — checked by the RPC.
     */
    subtype_code: z.string().min(1).max(100).nullable(),
    parent: coaPlanParentSchema.nullable(),
    /** null = the organization's currency. */
    currency: z.string().nullable(),
})
    .strict();
export const coaPlanSchema = z
    .object({
    accounts: z.array(coaPlanAccountSchema).min(1).max(COA_PLAN_MAX_ACCOUNTS),
})
    .strict()
    .superRefine((plan, ctx) => {
    const issue = (index, field, message) => ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["accounts", index, field], message });
    // Unique refs.
    const indexByRef = new Map();
    plan.accounts.forEach((a, i) => {
        if (indexByRef.has(a.ref))
            issue(i, "ref", `Duplicate ref "${a.ref}"`);
        else
            indexByRef.set(a.ref, i);
    });
    plan.accounts.forEach((a, i) => {
        // A ref parent must be another row of this plan.
        if (a.parent && "ref" in a.parent) {
            if (a.parent.ref === a.ref)
                issue(i, "parent", `Account "${a.ref}" cannot be its own parent`);
            else if (!indexByRef.has(a.parent.ref))
                issue(i, "parent", `Parent ref "${a.parent.ref}" is not in the plan`);
        }
        // A top-level row names its subtype (a child's is checked by the RPC).
        if (!a.parent && a.subtype_code === null) {
            issue(i, "subtype_code", "A top-level account needs a subtype_code");
        }
        // Supported currency.
        if (a.currency !== null && !SUPPORTED_CURRENCIES.includes(a.currency)) {
            issue(i, "currency", `Unsupported currency "${a.currency}"`);
        }
    });
    // No cycles through ref parents (self-parent is reported above).
    const parentRef = new Map();
    for (const a of plan.accounts) {
        if (a.parent && "ref" in a.parent && a.parent.ref !== a.ref)
            parentRef.set(a.ref, a.parent.ref);
    }
    plan.accounts.forEach((a, i) => {
        const seen = new Set([a.ref]);
        let cur = parentRef.get(a.ref);
        while (cur !== undefined) {
            if (seen.has(cur)) {
                if (cur === a.ref)
                    issue(i, "parent", `Account "${a.ref}" is part of a parent cycle`);
                break;
            }
            seen.add(cur);
            cur = parentRef.get(cur);
        }
    });
});
/**
 * Body for POST /chart-of-accounts/orgs/:orgId/plans/commit — the plan plus the
 * previewed hash. `message_id` (the Ré card's assistant message) switches the
 * route to the proposal commit (lib/coaPlan.ts `commitCoaProposal`): the STORED
 * plan is committed, `hash` must equal the stored hash, and the message is
 * stamped `coa_proposal.committed` — the same helper Ré's create_accounts uses.
 */
export const commitCoaPlanSchema = z
    .object({
    plan: coaPlanSchema,
    hash: z.string().regex(/^[0-9a-f]{64}$/),
    message_id: uuid.optional(),
})
    .strict();
/**
 * What `apply_chart_accounts_plan` returns (dry run and commit alike): one row
 * per plan account in plan order — `account_id` is null on a dry run (the
 * rows never existed) — the DUPLICATE_ACCOUNT warnings, and the hash over the
 * {ref: account_number} pairs (computed in SQL only; carried, never derived).
 * Unknown keys are stripped, so a later RPC field does not break a reader.
 */
export const coaPlanResultRowSchema = z.object({
    ref: z.string(),
    account_id: uuid.nullable(),
    account_number: z.string().nullable(),
    name: z.string(),
    subtype_code: z.string().nullable(),
    parent_ref: z.string().nullable(),
    parent_account_id: uuid.nullable(),
    currency: z.string().nullable(),
});
export const coaPlanWarningSchema = z.object({
    ref: z.string(),
    code: z.string(),
    name: z.string(),
    existing_account_id: uuid.nullable().optional(),
    existing_account_number: z.string().nullable().optional(),
    existing_account_name: z.string().nullable().optional(),
});
export const coaPlanResultSchema = z.object({
    rows: z.array(coaPlanResultRowSchema),
    warnings: z.array(coaPlanWarningSchema),
    hash: z.string(),
});
/**
 * `bookkeeper_messages.coa_proposal` — the last successful propose_accounts
 * preview of an assistant turn, persisted with REAL names (never pseudonyms):
 * the plan the card's Create button sends back, the previewed hash it sends
 * with it, and the preview rows + warnings the card shows.
 */
/** Who committed a proposal: the card's Create button, or Ré's create_accounts tool. */
export const COA_PROPOSAL_COMMITTERS = ["button", "re"];
/**
 * `coa_proposal.committed` — stamped by `commitCoaProposal` (lib/coaPlan.ts)
 * after a successful commit: when, by which path, and the created rows (with
 * their account ids and numbers). A UX marker for the card, not an audit trail
 * (account_audit is that).
 */
export const coaProposalCommittedSchema = z.object({
    at: z.string(),
    by: z.enum(COA_PROPOSAL_COMMITTERS),
    rows: z.array(coaPlanResultRowSchema),
});
export const coaProposalSchema = z.object({
    plan: coaPlanSchema,
    hash: z.string(),
    rows: z.array(coaPlanResultRowSchema),
    warnings: z.array(coaPlanWarningSchema),
    // Declared so a stamped proposal keeps it: the schema strips unknown keys.
    committed: coaProposalCommittedSchema.optional(),
});
/**
 * Update-account body. Two kinds of field ride in one PATCH:
 *
 *   * plain columns (name, description, flags, tax rate, currency) — the route
 *     writes them directly (Pattern B+);
 *   * classification — `type_id` / `subtype_id` / `parent_account_id` route
 *     through the SECURITY DEFINER `update_account_classification` RPC
 *     (Pattern C), which inherits the parent's type on a re-parent, cascades a
 *     type change to descendants, and refuses cross-category parents, cycles,
 *     depth > 3, system/OBE accounts, feed-linked Bank/Credit Cards accounts
 *     leaving the feed types, and a subtype of another type; it renumbers the
 *     moved subtree.
 *
 * `category_id` and `normal_balance` stay LOCKED on edit (they drive report
 * membership and balance signs) and are not accepted. `normal_balance_choice`
 * answers a NORMAL_BALANCE_CHOICE_REQUIRED refusal (see below) and is only
 * read with a classification change. When any classification
 * field is present, `type_id` is required unless `parent_account_id` names a
 * parent (the inherit case — the RPC copies the parent's type/subtype).
 *
 * opening_balance/opening_balance_date route through set_account_opening_balance
 * (journal reverse+repost), never the retired columns; the RPC no-ops when the
 * value is unchanged.
 */
export const CLASSIFICATION_FIELDS = ["type_id", "subtype_id", "parent_account_id"];
/**
 * What to do with a MANUAL normal-balance override that a reclassification
 * would overwrite (Clay's ruling 2026-10-06 — the user is asked):
 * `keep` the stored side, or `reset` to the standard side of the new
 * classification. Only consulted for an account whose stored side differs
 * from the rule under its old classification; a contra target subtype always
 * takes its own side. docs/CHART_OF_ACCOUNTS.md "Contra subtypes".
 */
export const normalBalanceChoiceSchema = z.enum(["keep", "reset"]);
/**
 * Refusal code of `update_account_classification` (and the PATCH route's 409)
 * when a reclassification would overwrite a manual normal-balance override and
 * no `normal_balance_choice` was sent. The body carries `accounts`.
 */
export const NORMAL_BALANCE_CHOICE_REQUIRED = "NORMAL_BALANCE_CHOICE_REQUIRED";
/**
 * Refusal code of `update_account_classification` (400) and of the template
 * cascade (`coa_template_account_cascade_classification`, SQLSTATE RV007 with
 * the message `CONTRA_SUB_ACCOUNTS_BLOCK|<json array>`) when a
 * reclassification would drop the OWN contra subtype of sub-accounts — a type
 * change, or the parent moved onto that very subtype (Clay's ruling
 * 2026-10-06, 20261006220000). Nothing was written; the body carries
 * `accounts`: move or reclassify those sub-accounts first.
 */
export const CONTRA_SUB_ACCOUNTS_BLOCK = "CONTRA_SUB_ACCOUNTS_BLOCK";
/**
 * The whitelisted `accounts` of a CONTRA_SUB_ACCOUNTS_BLOCK refusal: only the
 * four fields, only rows with an id and a name (anything else is dropped).
 */
export function contraSubAccountBlocks(raw) {
    if (!Array.isArray(raw))
        return [];
    const str = (v) => (typeof v === "string" ? v : null);
    return raw.flatMap((row) => {
        if (!row || typeof row !== "object")
            return [];
        const r = row;
        const id = str(r.id);
        const name = str(r.account_name);
        if (!id || name === null)
            return [];
        return [{ id, account_name: name, account_number: str(r.account_number), subtype_code: str(r.subtype_code) }];
    });
}
/**
 * Parse the template cascade's RV007 message `CONTRA_SUB_ACCOUNTS_BLOCK|<json>`
 * into its whitelisted accounts; null when the message is not that refusal.
 */
export function parseContraSubAccountsBlock(message) {
    const prefix = `${CONTRA_SUB_ACCOUNTS_BLOCK}|`;
    if (!message || !message.startsWith(prefix))
        return null;
    try {
        return contraSubAccountBlocks(JSON.parse(message.slice(prefix.length)));
    }
    catch {
        return [];
    }
}
export const updateAccountSchema = z
    .object({
    account_name: z.string().min(1).optional(),
    account_description: z.string().nullish(),
    can_have_children: z.boolean().optional(),
    requires_project: z.boolean().optional(),
    opening_balance: z.number().optional(),
    opening_balance_date: z.string().nullish(),
    default_tax_rate_id: uuid.nullish(),
    currency: z.string().nullish(),
    type_id: uuid.optional(),
    subtype_id: uuid.nullish(),
    parent_account_id: uuid.nullish(),
    normal_balance_choice: normalBalanceChoiceSchema.optional(),
})
    .strict()
    .superRefine((body, ctx) => {
    const touchesClassification = CLASSIFICATION_FIELDS.some((f) => body[f] !== undefined);
    if (touchesClassification && !body.type_id && !body.parent_account_id) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["type_id"],
            message: "type_id is required unless parent_account_id names a parent to inherit from",
        });
    }
});
/**
 * Apply-template body for POST /orgs/:orgId/apply-template. Wraps the
 * `apply_chart_of_accounts_template(p_organization_id, p_template_id)` RPC, which
 * is SECURITY INVOKER and inserts into the backend-locked `chart_of_accounts`
 * table — so it must run through the backend (the per-request client attaches the
 * x-backend-auth HMAC). organization_id comes from the `:orgId` route param, not
 * the body.
 */
export const applyChartOfAccountsTemplateSchema = z
    .object({
    template_id: uuid,
})
    .strict();
/** Query params for GET /orgs/:orgId/accounts. `includeSystem=true` returns FX/system accounts too. */
export const listAccountsQuerySchema = z.object({
    includeSystem: z.enum(["true", "false"]).optional(),
});
/** Query params for GET /reference/types. */
export const accountTypesQuerySchema = z.object({
    categoryId: uuid.optional(),
});
/** Query params for GET /reference/subtypes. `fetchAll=true` returns every subtype (grid resolution). */
export const accountSubtypesQuerySchema = z.object({
    typeId: uuid.optional(),
    fetchAll: z.enum(["true", "false"]).optional(),
});
/**
 * Body of PATCH /orgs/:orgId/numbering-settings — the two chart-of-accounts
 * display/numbering settings on `organizations`. At least one key. Changing
 * the scheme renumbers every account of the org (the AFTER UPDATE trigger
 * `trg_organizations_renumber_accounts`).
 */
export const updateNumberingSettingsSchema = z
    .object({
    account_numbering_scheme: z.enum(ACCOUNT_NUMBERING_SCHEMES).optional(),
    show_account_numbers: z.boolean().optional(),
})
    .strict()
    .refine((v) => v.account_numbering_scheme !== undefined || v.show_account_numbers !== undefined, { message: "Nothing to update" });
//# sourceMappingURL=chartOfAccounts.js.map